import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { database } from "../../firebase";
import { ref, onValue, update, remove } from "firebase/database";
import { getAuth } from "firebase/auth";
import "./AdminUserSelector.css";

export default function AdminUserSelector({ onUserSelected, onSignOut }) {
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const scansRef = ref(database, "data/scans");

        const unsubscribe = onValue(scansRef, (snapshot) => {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            let userIDs = [];

            if (snapshot.exists()) {
                userIDs = Object.keys(snapshot.val());
            }

            // Always include current admin's userID if not already present
            // This ensures if DB is empty, the admin can still select themselves to start scanning
            if (currentUser && currentUser.email) {
                const saneEmail = currentUser.email.replace(/\./g, "-");
                if (!userIDs.includes(saneEmail)) {
                    userIDs.unshift(saneEmail);
                }
            }

            setAvailableUsers(userIDs);
            // If currently selected user is not in the new list, select the first one
            // Or if nothing selected yet, select the first
            if (userIDs.length > 0 && (!selectedUser || !userIDs.includes(selectedUser))) {
                setSelectedUser(userIDs[0]);
            }

            setLoading(false);
        }, (error) => {
            console.error("Error fetching scans:", error);
            // Even on error, try to show current user so admin isn't stuck
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (currentUser && currentUser.email) {
                const saneEmail = currentUser.email.replace(/\./g, "-");
                setAvailableUsers([saneEmail]);
                setSelectedUser(saneEmail);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleSelect = () => {
        if (selectedUser) {
            onUserSelected(selectedUser);
        }
    };

    const [showManageAdmins, setShowManageAdmins] = useState(false);
    const [adminsList, setAdminsList] = useState([]);
    const [newAdminEmail, setNewAdminEmail] = useState("");

    useEffect(() => {
        if (!showManageAdmins) return;
        const adminsRef = ref(database, "data/settings/admins");
        const unsubscribe = onValue(adminsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                // Filter only true values
                const list = Object.keys(data).filter((k) => data[k] === true);
                setAdminsList(list);
            } else {
                setAdminsList([]);
            }
        });
        return () => unsubscribe();
    }, [showManageAdmins]);

    const handleAddAdmin = async () => {
        if (!newAdminEmail) return;
        // Basic email check
        if (!newAdminEmail.includes("@")) {
            alert("Invalid email");
            return;
        }
        const sanitized = newAdminEmail.replace(/\./g, "-");
        try {
            await update(ref(database, "data/settings/admins"), {
                [sanitized]: true,
            });
            setNewAdminEmail("");
        } catch (e) {
            alert("Error adding admin: " + e.message);
        }
    };

    const handleRemoveAdmin = async (key) => {
        // Prevent removing self
        const auth = getAuth();
        const currentUserEmail = auth.currentUser?.email;
        const sanitizedCurrentUser = currentUserEmail?.replace(/\./g, "-");

        if (key === sanitizedCurrentUser) {
            alert("You cannot remove yourself from the admin list and lock yourself out!");
            return;
        }

        // Prevent removing self if possible, or just warn
        if (!window.confirm(`Are you sure you want to remove admin access for ${key}?`)) {
            return;
        }
        try {
            await remove(ref(database, `data/settings/admins/${key}`));
        } catch (e) {
            alert("Error removing admin: " + e.message);
        }
    };

    const [searchTerm, setSearchTerm] = useState("");

    // Filter users based on search term
    const filteredUsers = availableUsers.filter(user =>
        user.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="admin-user-selector">
            <button
                className="aus-manage-btn"
                onClick={() => setShowManageAdmins(true)}
            >
                Manage Admins
            </button>

            <h2>Admin - Select User</h2>

            {loading && <div className="aus-loading">Loading users...</div>}

            {!loading && availableUsers.length === 0 && (
                <div className="aus-no-users">No users found in database.</div>
            )}

            {!loading && availableUsers.length > 0 && (
                <>
                    <div className="aus-field">
                        <label htmlFor="user-search">Search User:</label>
                        <input
                            id="user-search"
                            type="text"
                            list="user-datalist"
                            className="aus-input"
                            placeholder="Type to search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <datalist id="user-datalist">
                            {availableUsers.map(user => (
                                <option key={user} value={user} />
                            ))}
                        </datalist>
                    </div>

                    <div className="aus-field">
                        <label htmlFor="user-select">Select User ID:</label>
                        <select
                            id="user-select"
                            value={selectedUser}
                            onChange={(e) => setSelectedUser(e.target.value)}
                            className="aus-dropdown"
                        >
                            {/* If filtered list is empty, maybe show 'No matches' or keep empty */}
                            {filteredUsers.length === 0 && <option value="">No matches</option>}

                            {filteredUsers.map((userId) => (
                                <option key={userId} value={userId}>
                                    {userId}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="aus-actions">
                        <button className="aus-button-primary" onClick={handleSelect}>
                            View User Data
                        </button>
                        <button className="aus-button-secondary" onClick={onSignOut}>
                            Sign Out
                        </button>
                    </div>
                </>
            )}

            {/* Manage Admins Modal */}
            {showManageAdmins && (
                <div className="aus-modal-overlay">
                    <div className="aus-modal">
                        <button
                            className="aus-modal-close"
                            onClick={() => setShowManageAdmins(false)}
                        >
                            &times;
                        </button>
                        <h3>Manage Admins</h3>

                        <div className="aus-add-section">
                            <input
                                type="email"
                                className="aus-add-input"
                                placeholder="Enter admin email"
                                value={newAdminEmail}
                                onChange={(e) => setNewAdminEmail(e.target.value)}
                            />
                            <button className="aus-add-btn" onClick={handleAddAdmin}>
                                Add
                            </button>
                        </div>

                        <h4>Existing Admins</h4>
                        <ul className="aus-admin-list">
                            {adminsList.map(adminKey => (
                                <li key={adminKey} className="aus-admin-item">
                                    <span>{adminKey}</span>
                                    <button
                                        className="aus-remove-btn"
                                        onClick={() => handleRemoveAdmin(adminKey)}
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))}
                            {adminsList.length === 0 && (
                                <li className="aus-admin-item">No admins found other than you?</li>
                            )}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

AdminUserSelector.propTypes = {
    onUserSelected: PropTypes.func.isRequired,
    onSignOut: PropTypes.func,
};
